
/// <amd-module name="Environments/CreatePanel/Components/EditEnvironmentComponent" />
import * as React from "react";
import { EnvironmentInstance } from "DistributedTask/Client/Contracts/TaskAgent";

import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { EnvironmentsCreatePanelActionsCreator } from "Environments/CreatePanel/ActionsCreators/EnvironmentsCreatePanelActionsCreator";
import { EnvironmentDetailsViewComponent } from "Environments/CreatePanel/Components/EnvironmentDetailsViewComponent";

import * as Resources from "Environments/CreatePanel/Resources";
import { PageKey, getTitle } from "Environments/CreatePanel/Common/Utils";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Panel } from "VSSUI/Panel";
import { IButtonProps } from "VSSUI/Button";

interface  IEditEnvironmentComponentState extends IEnvironmentBaseComponentState{
    environmentName: string;
    environmentDescription: string;
    disabledSaveButton: boolean;
}

interface IEditEnvironmentProperties extends IEnvironmentBaseComponentProperties {
    onEnvironmentUpdated: (environment: EnvironmentInstance) => void;
    onDismissPanel: () => void;
    instanceId: string;
    environment: EnvironmentInstance;
}

export class EditEnvironmentComponent extends EnvironmentBaseComponent<IEditEnvironmentProperties, IEditEnvironmentComponentState> {
    
    public getInstanceId(context: IVssComponentContext) : string {
        return this.props.instanceId
    }

    constructor(props: IEditEnvironmentProperties, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            environmentName: this.props.environment.name,
            environmentDescription: this.props.environment.description,
            disabledSaveButton: true,
            errorMessages: {}
        };

        this._actionCreator = ActionCreatorManager.CreateActionCreator<EnvironmentsCreatePanelActionsCreator>(EnvironmentsCreatePanelActionsCreator, this._instanceId);
    }

    public render(): JSX.Element {
        return (
            <Panel titleProps = {getTitle(Resources.EditEnvironment)} onDismiss = {this.props.onDismissPanel} footerButtonProps = {this._getButtonProps()}> 
                <div className="panel-content flex-grow">
                    {this.getErrorMessageView(PageKey.EditEnvironmentView)}
                    <EnvironmentDetailsViewComponent 
                        instanceId={this._instanceId} 
                        onEnvironmentNameChange={this._onEnvironmentNameChange} 
                        onEnvironmentDescriptionChange={this._onEnvironmentDescriptionChange}
                        environmentName={this.state.environmentName}
                        environmentDescription={this.state.environmentDescription}
                    />
                </div>
            </Panel>
        );
    }

    public componentWillUnmount(): void {
        ActionCreatorManager.DeleteActionCreator<EnvironmentsCreatePanelActionsCreator>(EnvironmentsCreatePanelActionsCreator, this._instanceId);
        super.componentWillUnmount();
    }

    private _getButtonProps(): IButtonProps[] {
        let buttonsProps: IButtonProps[] = [];
        buttonsProps.push({
            ariaLabel: Resources.SaveButton, 
            onClick: this._saveEnvironmentButtonClick,
            text: Resources.SaveButton,
            primary: true,
            disabled: this.state.disabledSaveButton
        });
        return buttonsProps;
    }

    private _saveEnvironmentButtonClick = () => {
        this._actionCreator.updateEnvironment(this.context.pageContext, this.props.environment.id, this.state.environmentName, this.state.environmentDescription, this.props.onEnvironmentUpdated);
        this.setState({disabledSaveButton: true});
    }

    private _onEnvironmentNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this.setState({environmentName: newValue, disabledSaveButton: this._isSaveButtonDisable(newValue, this.state.environmentDescription)});
    }

    private _onEnvironmentDescriptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this.setState({environmentDescription: newValue, disabledSaveButton: this._isSaveButtonDisable(this.state.environmentName, newValue)});
    }

    private _isSaveButtonDisable(name: string, description: string): boolean {
        return !name || (this.props.environment.name === name && this.props.environment.description === description);
    }

    private _actionCreator: EnvironmentsCreatePanelActionsCreator;
}

VssComponent.register("editEnvironment", EditEnvironmentComponent);
